import React, { useCallback } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { useNavigate, useParams } from '@common/hooks';
import { LongTermTeamTable } from './long-term-team-table';
import { OnDemandTeamTable } from '@scheduling/pages/organization';
import { AppContent, ButtonGroup, Tabs } from '@common/components';
import { ExportTeamButtonContextProvider } from 'works/legacy/components/export-team-button/export-team-button-context';
import { ExportAssignmentsButton } from 'works/legacy/components/export-team-button';
import { OnDemandTeamPageActions } from '@scheduling/components/on-demand-team-page-actions';
import { useCurrentMembership, useCurrentEntity } from '@identity/hooks';
import { LineHeight } from '@common/tokens';

export function OrganizationTeamPage() {
  const { type } = useParams();

  useSetBreadcrumbs([{ name: 'Team', path: `/works/team/${type}` }]);

  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();
  const currentEntity = useCurrentEntity();

  const onDemandEnabled = currentEntity.onDemandEnabled;
  const showLongTermAssignments = currentMembership.canShowLongTermAssignments;

  const handleTabChange = useCallback(
    (key) => {
      navigate(`/works/team/${key}`);
    },
    [navigate]
  );

  const canCreateWorkers = currentMembership.canCreateWorkers;
  const canUploadWorkers = currentMembership.canUploadWorkers;

  const isTypeLongTerm = type === 'long_term';
  const isTypeOnDemand = type === 'on_demand';

  const renderExtraContent = () => {
    if (isTypeLongTerm) {
      return (
        <ButtonGroup>
          <ExportAssignmentsButton
            filterKey="ClientAdminTeamTable"
            requestTypes={['long_term']}
          />
          <div id="wk-portal-target:organization-team-page-long-term-extra-actions"></div>
        </ButtonGroup>
      );
    }

    if (isTypeOnDemand) {
      return (
        <OnDemandTeamPageActions
          canCreateWorkers={canCreateWorkers}
          canUploadWorkers={canUploadWorkers}
        />
      );
    }

    return null;
  };

  return (
    <AppContent>
      {onDemandEnabled && showLongTermAssignments && (
        <Tabs
          onChange={handleTabChange}
          activeKey={type || 'long_term'}
          className="TeamTabs"
          tabBarExtraContent={{
            right: renderExtraContent(),
          }}
        >
          <Tabs.Pane tab="Long Term" key="long_term">
            <section className="teamTables">
              <ExportTeamButtonContextProvider>
                <LongTermTeamTable triggerPortal={isTypeLongTerm} />
              </ExportTeamButtonContextProvider>
            </section>
          </Tabs.Pane>
          <Tabs.Pane tab="On Demand" key="on_demand">
            <section className="teamTables">
              <OnDemandTeamTable membershipError={!currentMembership} />
            </section>
          </Tabs.Pane>
        </Tabs>
      )}

      {onDemandEnabled && !showLongTermAssignments && (
        <OnDemandTeamTable
          showHeader
          canCreateWorkers={canCreateWorkers}
          canUploadWorkers={canUploadWorkers}
        />
      )}
      {!onDemandEnabled && showLongTermAssignments && (
        <ExportTeamButtonContextProvider>
          <LongTermTeamTable showHeader />
        </ExportTeamButtonContextProvider>
      )}
      <style jsx>{`
        :global(.TeamTabs .ant-tabs-extra-content) {
          line-height: ${LineHeight.xxl};
          align-self: flex-start;
        }
        :global(.add-staff-dropdown:hover) {
          opacity: 90%;
        }
      `}</style>
    </AppContent>
  );
}
